import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Sparkles,
  GraduationCap,
  Award,
  Users,
  TrendingUp,
  AlertCircle,
  Percent,
  RefreshCw,
} from 'lucide-react';
import {
  useGetKvsResultAllClassSummaryQuery,
  AllClassStageSummary,
  resultsSessionLabel,
} from '../services/kvsResultApi';
import { ScoreBandPanel, shareOf } from '../components/ScoreBandPanel';

const StageSelection: React.FC = () => {
  const navigate = useNavigate();

  const [academicYear, setAcademicYear] = useState<number>(2026);

  // Single call powers the entire screen — overview KPIs + all four stage cards.
  const { data, isLoading, isFetching, refetch } = useGetKvsResultAllClassSummaryQuery({ year: academicYear });

  const overview = data?.data.overview;
  const stages = data?.data.stages;

  const emptyStage: AllClassStageSummary = {
    totalStudents: 0,
    avgMarksPercentage: 0,
    excellentStudents: 0,
    needSupport: 0,
    excellentRate: 0,
    supportRate: 0,
  };

  const foundationStats = stages?.foundationStage ?? emptyStage;
  const prepStats = stages?.preparatoryStage ?? emptyStage;
  const middleStats = stages?.middleStage ?? emptyStage;
  const secondaryStats = stages?.secondaryStage ?? emptyStage;

  const ready = !isLoading && Boolean(data);

  // Overview rates from the stage counts, for the same single rounding as the cards.
  const stageList = [foundationStats, prepStats, middleStats, secondaryStats];
  const stagesTotal = stageList.reduce((sum, s) => sum + s.totalStudents, 0);
  const excellenceRate = shareOf(stageList.reduce((sum, s) => sum + s.excellentStudents, 0), stagesTotal);
  const supportRate = shareOf(stageList.reduce((sum, s) => sum + s.needSupport, 0), stagesTotal);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-8 animate-fadeIn">
      {/* Title & Filter Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            Student Holistic Stages Selection
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            National overview dashboard. Select a developmental stage to explore detailed academic and holistic analytics.
          </p>
        </div>

        {/* Interactive Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
            <span className="text-xs font-semibold text-gray-500">Academic Session</span>
            <select
              value={academicYear}
              onChange={(e) => setAcademicYear(Number(e.target.value))}
              className="text-xs font-bold text-gray-700 bg-transparent focus:outline-none cursor-pointer"
            >
              {/* The value is the results year; the label is the session it holds. */}
              <option value="2026">{resultsSessionLabel(2026)}</option>
              <option value="2025">{resultsSessionLabel(2025)}</option>
            </select>
          </div>

          <button
            onClick={() => refetch()}
            className="p-2 border border-gray-200 hover:border-indigo-600 rounded-xl hover:bg-indigo-50 text-gray-600 hover:text-indigo-600 transition"
            title="Refresh national statistics"
          >
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* National Overview KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          {
            title: 'Total Students Evaluated',
            value: (overview?.totalStudents ?? 0).toLocaleString(),
            desc: 'Across all stages & KVS schools',
            icon: Users,
            color: 'text-indigo-600 bg-indigo-50 border-indigo-100',
          },
          {
            title: 'National Average Competency',
            value: `${(overview?.nationalAverage ?? 0).toFixed(1)}%`,
            desc: 'Four stage averages, weighted by students',
            icon: Percent,
            color: 'text-emerald-600 bg-emerald-50 border-emerald-100',
          },
          {
            title: 'Academic Excellence Rate',
            value: `${excellenceRate.toFixed(1)}%`,
            desc: 'Students scoring above 90%',
            icon: TrendingUp,
            color: 'text-blue-600 bg-blue-50 border-blue-100',
          },
          {
            title: 'Attention & Support Needed',
            value: `${supportRate.toFixed(1)}%`,
            desc: 'Students scoring below 50%',
            icon: AlertCircle,
            color: 'text-rose-600 bg-rose-50 border-rose-100',
          },
        ].map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <div
              key={idx}
              className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4 hover:shadow-md hover:scale-[1.01] transition-all"
            >
              <div className={`p-3 rounded-xl border ${kpi.color.split(' ').slice(1).join(' ')}`}>
                <Icon className={`w-6 h-6 ${kpi.color.split(' ')[0]}`} />
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{kpi.title}</span>
                {!ready
                  ? <div className="mt-1 h-7 w-28 bg-gray-100 rounded-lg animate-pulse" />
                  : <p className="text-2xl font-extrabold text-gray-900 mt-0.5">{kpi.value}</p>
                }
                <p className="text-[10px] text-gray-500 mt-0.5">{kpi.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Four Stages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* 1. Foundation Stage */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between overflow-hidden group">
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600 border border-emerald-100">
                <BookOpen size={24} />
              </div>
              <span className="px-3 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full border border-emerald-100">
                Balvatika I to Class II
              </span>
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">Foundational Stage</h2>
              <p className="text-xs text-gray-400 mt-1">Foundational literacy, numeracy, socio-emotional development, and physical wellness.</p>
            </div>

            {/* Stats Block */}
            <div className="grid grid-cols-2 items-end gap-4 py-2 border-y border-gray-50">
              <div>
                <span className="text-[10px] font-semibold text-gray-400 uppercase">Total Students</span>
                {ready
                  ? <p className="text-lg font-bold text-gray-800">{foundationStats.totalStudents.toLocaleString()}</p>
                  : <div className="mt-1 h-5 w-20 bg-gray-100 rounded animate-pulse" />}
              </div>
              <div>
                <span className="inline-block px-1.5 py-0.5 rounded-md border border-emerald-100 bg-emerald-50 text-[10px] leading-tight font-bold text-emerald-700 uppercase">Overall Avg Competency</span>
                {ready
                  ? <p className="text-lg font-bold text-emerald-600">{foundationStats.avgMarksPercentage.toFixed(1)}%</p>
                  : <div className="mt-1 h-5 w-14 bg-gray-100 rounded animate-pulse" />}
              </div>
            </div>

            {/* Distribution */}
            {ready
              ? <ScoreBandPanel stats={foundationStats} stage="foundational" />
              : <div className="mt-3 h-24 rounded-xl border border-gray-200 bg-gray-100 animate-pulse" />}
          </div>

          <button
            onClick={() => navigate('..?source=stages')}
            className="w-full bg-emerald-50 hover:bg-emerald-600 hover:text-white transition-all py-4 px-6 flex justify-between items-center text-sm font-semibold text-emerald-700 border-t border-emerald-50"
          >
            <span>Open Foundational Stage Analysis</span>
            <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* 2. Preparatory Stage */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between overflow-hidden group">
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div className="p-3 bg-blue-50 rounded-xl text-blue-600 border border-blue-100">
                <Sparkles size={24} />
              </div>
              <span className="px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full border border-blue-100">
                Class III to Class V
              </span>
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors">Preparatory Stage</h2>
              <p className="text-xs text-gray-400 mt-1">Formative assessments, academic evaluation, and structured core competency tracking.</p>
            </div>

            {/* Stats Block */}
            <div className="grid grid-cols-2 items-end gap-4 py-2 border-y border-gray-50">
              <div>
                <span className="text-[10px] font-semibold text-gray-400 uppercase">Total Students</span>
                {ready
                  ? <p className="text-lg font-bold text-gray-800">{prepStats.totalStudents.toLocaleString()}</p>
                  : <div className="mt-1 h-5 w-20 bg-gray-100 rounded animate-pulse" />}
              </div>
              <div>
                <span className="inline-block px-1.5 py-0.5 rounded-md border border-blue-100 bg-blue-50 text-[10px] leading-tight font-bold text-blue-700 uppercase">Overall Avg Performance</span>
                {ready
                  ? <p className="text-lg font-bold text-blue-600">{prepStats.avgMarksPercentage.toFixed(1)}%</p>
                  : <div className="mt-1 h-5 w-14 bg-gray-100 rounded animate-pulse" />}
              </div>
            </div>

            {/* Distribution */}
            {ready
              ? <ScoreBandPanel stats={prepStats} stage="preparatory" />
              : <div className="mt-3 h-24 rounded-xl border border-gray-200 bg-gray-100 animate-pulse" />}
          </div>

          <button
            onClick={() => navigate('preparatory')}
            className="w-full bg-blue-50 hover:bg-blue-600 hover:text-white transition-all py-4 px-6 flex justify-between items-center text-sm font-semibold text-blue-700 border-t border-blue-50"
          >
            <span>Open Preparatory Stage Analysis</span>
            <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* 3. Middle Stage */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between overflow-hidden group">
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div className="p-3 bg-violet-50 rounded-xl text-violet-600 border border-violet-100">
                <GraduationCap size={24} />
              </div>
              <span className="px-3 py-1 text-xs font-semibold text-violet-700 bg-violet-50 rounded-full border border-violet-100">
                Class VI to Class VIII
              </span>
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 group-hover:text-violet-700 transition-colors">Middle Stage</h2>
              <p className="text-xs text-gray-400 mt-1">Interdisciplinary learning, vocational training, subject competency, and peer behavior maps.</p>
            </div>

            {/* Stats Block */}
            <div className="grid grid-cols-2 items-end gap-4 py-2 border-y border-gray-50">
              <div>
                <span className="text-[10px] font-semibold text-gray-400 uppercase">Total Students</span>
                {ready
                  ? <p className="text-lg font-bold text-gray-800">{middleStats.totalStudents.toLocaleString()}</p>
                  : <div className="mt-1 h-5 w-20 bg-gray-100 rounded animate-pulse" />}
              </div>
              <div>
                <span className="inline-block px-1.5 py-0.5 rounded-md border border-violet-100 bg-violet-50 text-[10px] leading-tight font-bold text-violet-700 uppercase">Overall Avg Performance</span>
                {ready
                  ? <p className="text-lg font-bold text-violet-600">{middleStats.avgMarksPercentage.toFixed(1)}%</p>
                  : <div className="mt-1 h-5 w-14 bg-gray-100 rounded animate-pulse" />}
              </div>
            </div>

            {/* Distribution */}
            {ready
              ? <ScoreBandPanel stats={middleStats} stage="middle" />
              : <div className="mt-3 h-24 rounded-xl border border-gray-200 bg-gray-100 animate-pulse" />}
          </div>

          <button
            onClick={() => navigate('middle')}
            className="w-full bg-violet-50 hover:bg-violet-600 hover:text-white transition-all py-4 px-6 flex justify-between items-center text-sm font-semibold text-violet-700 border-t border-violet-50"
          >
            <span>Open Middle Stage Analysis</span>
            <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* 4. Secondary Stage */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between overflow-hidden group">
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div className="p-3 bg-amber-50 rounded-xl text-amber-600 border border-amber-100">
                <Award size={24} />
              </div>
              <span className="px-3 py-1 text-xs font-semibold text-amber-700 bg-amber-50 rounded-full border border-amber-100">
                Class IX and Class XI
              </span>
            </div>

            <div>
              <h2 className="text-xl font-bold text-gray-900 group-hover:text-amber-700 transition-colors">Secondary Stage</h2>
              <p className="text-xs text-gray-400 mt-1">High-stakes academic tracking, career stream options, and advanced scholastic profiles.</p>
            </div>

            {/* Stats Block */}
            <div className="grid grid-cols-2 items-end gap-4 py-2 border-y border-gray-50">
              <div>
                <span className="text-[10px] font-semibold text-gray-400 uppercase">Total Students</span>
                {ready
                  ? <p className="text-lg font-bold text-gray-800">{secondaryStats.totalStudents.toLocaleString()}</p>
                  : <div className="mt-1 h-5 w-20 bg-gray-100 rounded animate-pulse" />}
              </div>
              <div>
                <span className="inline-block px-1.5 py-0.5 rounded-md border border-amber-100 bg-amber-50 text-[10px] leading-tight font-bold text-amber-700 uppercase">Overall Avg Performance</span>
                {ready
                  ? <p className="text-lg font-bold text-amber-600">{secondaryStats.avgMarksPercentage.toFixed(1)}%</p>
                  : <div className="mt-1 h-5 w-14 bg-gray-100 rounded animate-pulse" />}
              </div>
            </div>

            {/* Distribution */}
            {ready
              ? <ScoreBandPanel stats={secondaryStats} stage="secondary" />
              : <div className="mt-3 h-24 rounded-xl border border-gray-200 bg-gray-100 animate-pulse" />}
          </div>

          <button
            onClick={() => navigate('secondary')}
            className="w-full bg-amber-50 hover:bg-amber-600 hover:text-white transition-all py-4 px-6 flex justify-between items-center text-sm font-semibold text-amber-700 border-t border-amber-50"
          >
            <span>Open Secondary Stage Analysis</span>
            <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

      </div>
    </div>
  );
};

export default StageSelection;
